export const LOCALES = [
  { id: "en", label: "EN" },
  { id: "zh-CN", label: "简" },
  { id: "zh-TW", label: "繁" },
];

export const messages = {
  en: {
    meta: {
      title: "NewFW — AI Data Infrastructure",
      description:
        "NewFW builds AI data infrastructure, firmware to tokens — CXL memory, QLC storage, cluster workloads, and LLM access.",
    },
    nav: {
      products: "Products",
      solutions: "Solutions",
      resources: "Resources",
      blog: "Blog",
      about: "About",
      contact: "Contact",
      openMenu: "Open menu",
      closeMenu: "Close menu",
    },
    kind: {
      product: "Product",
      solution: "Solutions",
    },
    productsNav: {
      bdf: "BDF QLC Storage",
      bdfDesc: "AI-path QLC appliance with TLC-grade performance",
      cxl: "CXL Memory",
      cxlDesc: "High-bandwidth, low-latency memory expansion and pooling",
      cwp: "Cloud Workload Platform",
      cwpDesc: "Workload management, live monitoring, testing, and analysis — one platform",
      token: "Token Service",
      tokenDesc: "Unified LLM API keys, routing, and quotas",
      firmware: "Firmware",
      firmwareDesc: "UEFI UniversalPayload and USF-based firmware stack",
    },
    resources: {
      brief: "BDF Datasheet",
      briefDesc: "QLC storage appliance datasheet",
      console: "Token Service console",
      consoleDesc: "Open the LLM gateway",
      blog: "Blog",
      blogDesc: "MLPerf results and engineering notes",
    },
    hero: {
      title: "AI data infrastructure,",
      titleAccent: "firmware to tokens.",
      desc: "QLC storage, CXL memory, cluster software, and firmware — built for AI inference and training at scale.",
      explore: "Explore Products",
      flow: [
        { tag: "Firmware", title: "UEFI / USF boot stack", note: "Payload · USF · SlimBMC", href: "#firmware" },
        { tag: "CXL", title: "Memory expansion & pooling", note: "CXL.mem · CXL.io", href: "#cxl" },
        { tag: "BDF", title: "QLC storage for AI paths", note: "118 GB/s · WAF ≈ 1.0", href: "/products/bdf" },
        { tag: "CWP", title: "Cluster workload platform", note: "Hosts · GPU · BMC", href: "#cwp" },
        { tag: "Token Service", title: "LLM access layer", note: "OpenAI API · routing", href: "#token" },
      ],
    },
    grid: {
      kicker: "Products & solutions",
      title: "What we build",
      subtitle:
        "Products and solutions that take AI data infrastructure from firmware to tokens.",
      bdfDesc:
        "A high-capacity QLC appliance that delivers TLC-grade performance and long endurance for AI inference, training, and cluster storage, at a cost that scales.",
      cxlDesc:
        "CXL Type-3 expansion with CXL.mem and CXL.io — high-bandwidth, low-latency memory expansion and pooling for machine learning and cloud infrastructure.",
      cwpDesc:
        "A platform for workload management, live multi-dimensional monitoring, automated testing, and performance analysis.",
      tokenDesc:
        "Mint, quota, and route LLM API keys across public models and private GPU clusters through one OpenAI-compatible gateway.",
      firmwareDesc:
        "UEFI UniversalPayload and a USF-based stack — open, upgradable firmware from boot to board management.",
    },
    bdf: {
      eyebrow: "BDF · Big Data Foundation",
      subtitle: "QLC Storage Appliance for AI Data Paths",
      desc: "BDF is a software-defined QLC storage appliance built for AI inference and training. Host-side BDF FTL write shaping absorbs random writes in Gen5 TLC cache, then flushes sequential streams to QLC base — so you get {{QLC capacity economics}} with {{TLC-grade performance}} and a write amplification factor of ≈1.0.",
      h1: "One appliance for AI inference, training, and cluster storage.",
      h2: "TLC-grade performance at QLC prices — speed and capacity, no trade-off.",
      h3: "Extends SSD lifespan — fewer failures, less disruption in production.",
      button: "Explore BDF",
      imgAlt:
        "BDF software stack — applications, protocols, BDF Agent, BDF Storage Core, TLC cache and QLC base",
      m1: "Sequential read",
      m2: "4K random read IOPS",
      m3: "5X endurance on raw QLC",
      m4: "Usable capacity, 10% OP",
    },
    bdfScenarios: {
      kicker: "Use cases",
      title: "Where BDF Fits",
      subtitle:
        "One unified block platform for KV cache, checkpoints, virtualization, and distributed file systems.",
      i1Tag: "AI Inference",
      i1Title: "KV Cache Offload",
      i1Desc:
        "Large-block reads up to 73.38 GB/s and writes up to 25.64 GB/s on 1S (4FTL), so cache segments load and persist at PCIe-limited speed and cut time-to-first-token.",
      i2Tag: "AI Training",
      i2Title: "Checkpoint Persistence",
      i2Desc:
        "Symmetric RAID5F groups write a 100 GB checkpoint in under 4 seconds on 1S. Bursts land on FTL at full bandwidth and drain to QLC asynchronously — the GPU loop does not stall.",
      i3Tag: "Cloud-native",
      i3Title: "CSI, NVMe-oF, vhost",
      i3Desc:
        "Standard 4K block devices for Kubernetes CSI, VMware / KVM / Hyper-V, and NFS or MinIO gateways. One appliance, file, block, and object.",
      i4Tag: "HPC / AI clusters",
      i4Title: "Distributed File Systems",
      i4Desc:
        "Present RAID5F-protected NVMe targets to Lustre, BeeGFS, or JuiceFS. Durability stays on the appliance, so the fabric is not burned on 3× replication.",
    },
    bdfPage: {
      metaTitle: "BDF QLC Storage — NewFW",
      metaDescription:
        "BDF is a software-defined QLC storage appliance for AI data paths — TLC-grade performance at QLC economics, WAF ≈ 1.0.",
      eyebrow: "BDF · Big Data Foundation",
      title: "QLC storage",
      titleAccent: "for AI data paths.",
      lead: "A software-defined appliance that shapes host writes through TLC cache, then streams sequential I/O to QLC base — capacity economics without giving up performance.",
      heroImgAlt: "BDF 2S 24-bay QLC storage appliance",
      download: "Download Datasheet",
      contact: "Contact",
      metrics: [
        { value: "118 GB/s", label: "Sequential read" },
        { value: "5.0M", label: "4K random read IOPS" },
        { value: "WAF ≈ 1.0", label: "5X endurance on raw QLC" },
        { value: "1.6 PB", label: "Usable capacity, 10% OP" },
      ],
      overview: {
        kicker: "Overview",
        title: "The storage challenge in AI",
        lead: "Inference needs petabyte-class KV cache with microsecond paths. Training dumps multi-gigabyte checkpoints without stalling the GPU loop.",
        body: "All-TLC arrays deliver the speed but not the $/TB. Raw QLC is cheap, then collapses under random writes. BDF closes that gap with host-side {{BDF FTL}} write shaping — random I/O lands in Gen5 TLC cache, aggregates into sequential streams, and flushes to QLC base so you keep {{QLC economics}} with {{TLC-grade performance}} and write amplification near 1.0.",
      },
      family: {
        kicker: "Configurations",
        title: "Product family",
        subtitle:
          "1S and 2S share one software stack. 2S is a NUMA-symmetric replica of the 1S 10+2 layout.",
        columns: ["", "BDF 1S 12-Bay", "BDF 2S 24-Bay"],
        rows: [
          ["Form factor", "2U rack", "2U rack"],
          ["Drive bays", "12× NVMe (10 QLC + 2 TLC / 8 QLC + 4 TLC)", "24× NVMe (20 QLC + 4 TLC)"],
          [
            "CPU",
            "Intel® Xeon® 6400 series processors (Sapphire Rapids)\nIntel® Xeon® 6700 series processors (Granite Rapids)",
            "Dual Intel® Xeon® 6400 series processors (Sapphire Rapids)\nDual Intel® Xeon® 6700 series processors (Granite Rapids)",
          ],
          ["DRAM", "256 → 1024 GB DDR5", "512 → 2048 GB DDR5"],
          ["TLC cache", "D7-PS1030 6.4 TB × 2", "D7-PS1030 6.4 TB × 4"],
          [
            "QLC storage",
            "D5-P5336 × 10 (up to 122.88 TB)",
            "D5-P5336 × 20 (up to 122.88 TB)",
          ],
          ["Raw base", "307 / 614 / 1229 TB", "614 / 1229 / 2458 TB"],
          ["Usable, OP 10%", "~208 / ~416 / ~832 TB", "~416 / ~832 / ~1664 TB"],
        ],
        note: "For write-heavy workloads, 20% OP is recommended for GC headroom and longer drive life.",
        driveNote: "D7-PS1030 and D5-P5336 are Solidigm SSDs.",
      },
      hardware: {
        kicker: "Hardware",
        title: "Two-tier architecture: TLC write performance, QLC economics",
        subtitle:
          "A TLC cache tier for the write path. A QLC base tier for capacity, endurance, and cost.",
        points: [
          {
            title: "TLC cache tier",
            desc: "TLC cache takes the write path at extreme performance and low latency — random and burst I/O land here first.",
          },
          {
            title: "QLC base tier",
            desc: "High-density QLC holds capacity. Full-stripe writes keep endurance high and write amplification low.",
          },
          {
            title: "BDF FTL",
            desc: "BDF FTL shapes host writes so QLC sees sequential traffic — WAF ≈ 1.0 and 5× endurance versus raw QLC.",
          },
          {
            title: "Data protection",
            desc: "Cache uses RAID 1 or RAID 10. Base uses RAID5F or RAID6F, with full-stripe writes aligned to flash.",
          },
        ],
      },
      software: {
        kicker: "Software",
        title: "Four-layer SPDK stack",
        subtitle: "User-space poll-mode path from protocol to media.",
        body: "SPDK reactors bypass the kernel I/O stack. {{BDF FTL}} absorbs random writes in TLC cache, packs them into large sequential units, and flushes to QLC — so base drives only see writes that match their native behavior. Management covers a web UI, {{bdf_cli}}, and Grafana telemetry across FTL instances, RAID groups, and NVMe drives.",
      },
      perf: {
        kicker: "Performance",
        title: "BDF Appliance Performance",
        columns: ["Metric", "1S (4 FTL)", "2S (8 FTL)"],
        rows: [
          ["Usable (OP 10%)", "166 TB", "332 TB"],
          ["1M sequential read", "59.2 GB/s", "118 GB/s"],
          ["1M sequential write", "12.72 GB/s", "25 GB/s"],
          ["4K random read", "2.52M IOPS", "5.0M IOPS"],
          ["4K random write", "1.27M IOPS", "2.5M IOPS"],
        ],
        insights: [
          "Sequential read sits at the QLC PCIe Gen4 ceiling — 8 × 7 GB/s — so the path is media-bound, not software-bound.",
          "Large-block write reaches 12.72 GB/s on 1S: a 100 GB checkpoint lands in about eight seconds.",
          "2S scales as NUMA × 2 on the same 8+4 layout — 118 GB/s sequential read and 5.0M 4K random read IOPS.",
        ],
      },
      adv: {
        kicker: "Advantages",
        title: "QLC economics without the usual trade-offs",
        subtitle: "Host-side FTL write shaping versus raw QLC under MDRAID5.",
        columns: ["Metric", "Raw QLC (MDRAID5)", "BDF FTL", "Improvement"],
        rows: [
          ["4K random write", "72.6K IOPS", "514K IOPS", "7.1×"],
          ["128K random write", "0.34 GB/s", "6.98 GB/s", "20×"],
          ["Sequential write", "1.93 GB/s", "6.94 GB/s", "3.6×"],
          ["Write amplification", "3–5×", "≈1.0", "3–5× lower"],
          ["Effective drive life", "0.56 DWPD baseline", "Extended by WAF reduction", "5×"],
        ],
      },
    },
    cxl: {
      eyebrow: "CXL Memory · platform capability",
      subtitle: "Expand Memory for AI Servers",
      desc: "A CXL Type-3 memory expansion controller with {{CXL.mem}} and {{CXL.io}} — high-bandwidth, low-latency expansion and pooling for machine learning, cloud infrastructure, and other data-intensive workloads.",
      h1: "CXL Type-3 controller with CXL, DDR, and an on-chip RISC-V microprocessor",
      h2: "CXL 1.1 / 2.0 / 3.2, CXL.mem and CXL.io, on PCIe 6.2 up to 64 GT/s (x8 or 2×x4)",
      h3: "JEDEC DDR5 up to DDR5-8000 on UDIMM, RDIMM, or on-board DRAM",
      button: "Contact",
      p1Title: "Type-3 expansion controller",
      p1Desc:
        "CXL and DDR controllers plus a RISC-V core on one die, with on-chip PVT monitoring, in a 1211-ball FCBGA package.",
      p2Title: "CXL controller",
      p2Desc:
        "Compliant with CXL 1.1, 2.0, and 3.2; supports CXL.mem and CXL.io. Designed on PCIe 6.2, up to 64 GT/s on an x8 port or two x4 ports.",
      p3Title: "DDR memory controller",
      p3Desc:
        "JEDEC DDR5 up to DDR5-8000 on UDIMM, RDIMM, or on-board DRAM, with low-power I/O equalization.",
      p4Title: "Typical applications",
      p4Desc:
        "Memory expansion and pooling on AIC cards or EDSFF modules, for in-memory databases, cloud, and emerging datacenter workloads.",
    },
    cwp: {
      eyebrow: "CWP",
      subtitle: "Cloud Workload Platform",
      desc: "CWP is a powerful platform that unifies {{workload management}}, end-to-end multi-dimensional {{real-time monitoring}}, automated testing, and performance analysis. It makes every run visible — CPU load, memory pressure, disk I/O curves, and dozens of other key signals.",
      h1: "Manage large, multi-region clusters from a single console.",
      h2: "Manage a full range of workloads, from GPU validation and SSD performance tests to cluster jobs on Kubernetes and Slurm.",
      h3: "Full-process multi-dimensional live monitoring",
      h4: "Automated testing and performance analysis in one place",
      imgAlt: "NewFW Cloud Workload Platform architecture",
    },
    cwpUse: {
      kicker: "Where it shines",
      title: "What CWP Is Built For",
      subtitle: "The same platform behind cluster jobs, factory burn-in, AI benchmarks, and day-to-day cluster visibility.",
      i1Tag: "Cluster",
      i1Title: "Workload management",
      i1Desc:
        "Define the job as a Recipe, attach tools / datasets / models / containers, and run it across the cluster with full history.",
      i2Tag: "Factory",
      i2Title: "Automated testing",
      i2Desc:
        "Turn board and component checks into repeatable runs — catch hardware issues early with a clear pass-rate trail.",
      i3Tag: "Analysis",
      i3Title: "Performance analysis",
      i3Desc:
        "See why a run stalled: correlate steps with CPU, memory pressure, disk I/O, and GPU signals on one timeline.",
      i4Tag: "Observe",
      i4Title: "Live visibility",
      i4Desc:
        "Host, GPU, disk, and BMC out-of-band metrics stay beside the workload — including BKBC host configuration context.",
    },
    cwpPage: {
      metaTitle: "CWP — Cloud Workload Platform — NewFW",
      metaDescription:
        "CWP manages tens of thousands of hosts, orchestrates Recipes, keeps step logs beside host / GPU / SSD / BMC telemetry, and installs a BKBC baseline OS so the cluster stays consistent.",
      eyebrow: "CWP · Cloud Workload Platform",
      title: "Cluster workloads,",
      titleAccent: "same environment, full trail.",
      lead: "CWP manages tens of thousands of hosts. Agents install and update themselves; Recipes dispatch automatically. Every step leaves a log next to host / GPU / SSD load in the test window — and BMC if the OS is down. BKBC is the baseline OS: install it, and the drivers and software are already there.",
      contact: "Talk to us",
      howCta: "See the four layers",
      hero: {
        imgAlt:
          "CWP console showing cluster hosts, Recipe runs, and live host / GPU / disk monitoring",
        caption:
          "One console — cluster Agents, Recipe runs, and host / GPU / SSD / BMC telemetry.",
        panes: [
          { tag: "Cluster", title: "10,000+ hosts" },
          { tag: "Recipe", title: "Tools · containers · datasets · models" },
          { tag: "Observe", title: "Steps + host / GPU / SSD + BMC" },
          { tag: "BKBC", title: "Baseline OS" },
        ],
      },
      facts: [
        { value: "10,000+", label: "Hosts in one cluster" },
        { value: "Agent", label: "Auto install, update, and dispatch" },
        { value: "Recipe", label: "Commands · tools · containers · datasets · models" },
        { value: "BKBC", label: "Baseline OS — drivers and software included" },
      ],
      overview: {
        kicker: "The gap",
        title: "The job, the box, and the environment are still three problems",
        lead:
          "Scripts on one host, Grafana on another, a different CUDA on the third. Results don't compare, and a fail has no trail.",
        body:
          "CWP closes that loop. {{BKBC}} makes the OS the same. {{Agents}} cover the cluster — auto-install, auto-update, auto-dispatch. A {{Recipe}} is the job, not a wiki page. {{Full-chain telemetry}} is the evidence: step logs, test-window host / GPU / SSD load, and BMC out-of-band.",
      },
      how: {
        kicker: "How it layers",
        title: "Baseline first, then the cluster, then the run",
        subtitle:
          "Four layers. Skip one, and the others stop being comparable.",
        body:
          "Install {{BKBC}} so every host starts from the same OS. Cover the cluster with {{Agents}} that install, update, and dispatch themselves. Write the workload as a {{Recipe}}. Keep the {{full-chain trail}} so the next run is a comparison, not a reconstruction.",
        steps: [
          {
            title: "Install BKBC",
            desc: "A baseline OS image with the drivers and software the workload needs already in place.",
          },
          {
            title: "Cover the cluster",
            desc: "Agents auto-install and auto-update across tens of thousands of hosts, then dispatch workloads by policy.",
          },
          {
            title: "Author the Recipe",
            desc: "System commands, plus tools, containers, datasets, and models when the job is more than a script.",
          },
          {
            title: "Keep the trail",
            desc: "Step-by-step logs, host / GPU / SSD load in the same test window, and BMC out-of-band.",
          },
        ],
        diagramAlt: "CWP four layers from BKBC baseline OS to full-chain telemetry",
      },
      pillars: {
        kicker: "What CWP does",
        title: "Four things. One product.",
        subtitle:
          "Not four modules bolted together — the cluster only works if all four are in place.",
        problem: "The problem",
        items: [
          {
            id: "cluster",
            tag: "Cluster",
            title: "Manage tens of thousands of hosts",
            problem:
              "SSH one box at a time. Plugins nobody upgrades. A workload that reaches the hosts that happened to be logged into.",
            body: "CWP is the cluster console. Agents {{download and update themselves}}. Workloads {{dispatch automatically}} to matching hosts — by GPU type, region, or Agent ID.",
            points: [
              "Tens of thousands of hosts in one console",
              "Agent auto-install and auto-update",
              "Policy-based dispatch, not one-by-one SSH",
            ],
            diagramAlt: "CWP console dispatching work to host Agents",
          },
          {
            id: "workload",
            tag: "Workload",
            title: "Orchestrate the job as a Recipe",
            problem:
              "Complex jobs live in shell scripts and whoever last touched the box. Two hosts run two versions of the same job.",
            body: "A Recipe is a {{versioned, multi-step}} job. Start with {{system commands}}. Attach {{tools, containers, datasets, and models}} from the resource center when the workload is more than a script. Dispatch it; every run lands in history.",
            points: [
              "System commands as the base",
              "Tools, containers, datasets, and models for complex jobs",
              "Versioned Recipes with a full execution history",
            ],
            diagramAlt: "CWP Recipe from authoring to host execution",
          },
          {
            id: "observe",
            tag: "Observe",
            title: "Full-chain logs and telemetry",
            problem:
              "The step log is in one place, Grafana in another, BMC in a third. None of them share a clock.",
            body: "CWP collects {{step-by-step execution logs}} and, in the same {{test window}}, records host, GPU, and SSD load. {{BMC / Redfish}} stays up when the OS does not.",
            points: [
              "Step logs for every task and subtask",
              "Host, GPU, and SSD load aligned to the test window",
              "BMC out-of-band, independent of the OS",
            ],
            diagramAlt: "CWP step logs beside host, GPU, SSD, and BMC telemetry",
          },
          {
            id: "bkbc",
            tag: "BKBC",
            title: "Install a baseline system",
            problem:
              "Same Recipe, different CUDA, driver, or toolkit — results that cannot be compared, and a cluster nobody can reproduce.",
            body: "BKBC (also called BKC) is a {{Best Known Configuration}} — a baseline OS image. Users {{install the system through BKBC}}; the drivers and software the workload needs are already there. No per-box setup. The cluster stays consistent.",
            points: [
              "Baseline OS image, not a config snapshot",
              "Drivers and software pre-installed",
              "Same environment for every Recipe on every host",
            ],
            diagramAlt: "BKBC baseline OS image installed onto cluster hosts",
          },
        ],
      },
      architecture: {
        kicker: "Architecture",
        title: "Console, Agent, hardware",
        subtitle:
          "From the cluster console down to the box — including BMC out-of-band.",
        body: "The console is where Recipes, resources, faults, logs, and dashboards sit. Platform services reach every {{host Agent}} — install, update, dispatch. Agents run the work and stream host / GPU / SSD / BMC signals. {{BKBC}} is how the host starts: a baseline OS, not a note in a wiki.",
        layers: [
          "Console — Recipes, cluster Agents, live monitoring",
          "Platform services — Agent lifecycle, assets, BKBC, dispatch",
          "Host Agent — execution, collectors, logs, metrics",
          "Hardware — GPU, SSD, BMC out-of-band, platform",
        ],
        diagramAlt: "CWP architecture: console, services, agent, hardware",
      },
      compare: {
        kicker: "Instead of the scavenger hunt",
        title: "What CWP replaces in the loop",
        subtitle: "The same cluster, without stitching four consoles after the fact.",
        columns: ["", "Scripts + SSH + Grafana", "CWP"],
        rows: [
          ["Hosts", "One-by-one SSH", "Agent auto-install / update, 10,000+ hosts"],
          ["Job", "Shell and wiki", "Versioned Recipe + tools / containers / datasets / models"],
          ["Environment", "Each box different", "BKBC baseline OS"],
          ["Logs", "Step log or Grafana, not both", "Step logs + test-window host / GPU / SSD"],
          ["Out-of-band", "A second BMC console", "BMC / Redfish on the same trail"],
          ["After a fail", "Reconstruct from logs", "Step, signal, and log already together"],
        ],
      },
      proof: {
        kicker: "In production use",
        title: "Behind NewFW's MLPerf Storage runs",
        body: "Each MLPerf Storage benchmark was packaged as a CWP Recipe — dispatched by the host Agent, monitored live, with every log and telemetry sample in the audit trail that went to MLCommons.",
        link: "Read the methodology",
        href: "/blog/mlperf-storage-v3",
      },
      close: {
        title: "Put the next cluster job on CWP",
        body: "Talk to us about a trial on your hosts — cluster workloads, factory burn-in, or AI benchmarks.",
        contact: "Contact",
      },
    },
    token: {
      eyebrow: "Token Service",
      subtitle: "Mint, Quota, and Route Every LLM Token",
      desc: "Token Service is NewFW’s LLM access layer. One {{OpenAI-compatible API}} fronts 50+ public models and your private vLLM / Ollama / GPU clusters. Issue API keys with group, quota, and expiry; route simple work on-prem and heavy reasoning to the cloud; keep sensitive prompts inside the boundary.",
      h1: "Batch-create keys, set ceilings, expire, and audit usage",
      h2: "Hybrid routing — public GPT / Claude / Gemini / DeepSeek / GLM plus local compute",
      h3: "One gateway for Claude Code, Cursor, Cline, OpenCode, and other agents",
      h4: "Wallet, model marketplace, and 99.9% availability-oriented failover",
      button: "Open console",
      p1Title: "One key, many models",
      p1Desc:
        "Applications keep a single SDK and endpoint while Token Service maps keys to model groups.",
      p2Title: "Cloud + private GPU",
      p2Desc:
        "BYOC: attach vLLM, Ollama, or TGI. Fail over when a vendor blips; pin secrets to local models.",
      p3Title: "Agent-ready",
      p3Desc:
        "SeaChart Code sits behind coding agents so the enterprise keeps token spend and data path under policy.",
      p4Title: "Ops you can see",
      p4Desc:
        "Dashboards, query logs, wallets, and channel health — tokens are an inventory, not a spreadsheet.",
    },
    firmware: {
      subtitle: "UEFI UniversalPayload Firmware",
      desc: "The NewFW UEFI UniversalPayload Firmware product utilizes open source projects {{Linux as the firmware}}, integrating {{u-root}}’s Go-based implementations of standard Linux tools and {{USF UniversalPayload}} for rapid validation and deployment. The same firmware program covers cloud firmware and SlimBMC board management.",
      imgAlt: "NewFW USF-based firmware architecture overview",
    },
    about: {
      kicker: "Company",
      title: "About NewFW",
      lead: "NewFW builds AI data infrastructure from firmware to tokens — host and BMC firmware, CXL memory, QLC storage on the AI data path, a cluster workload console, and an LLM token gateway.",
      talk: "Talk to us about BDF, firmware, or Token Service:",
    },
    blog: {
      kicker: "Insights",
      title: "Blog",
      subtitle:
        "MLPerf Storage results, CWP methodology, and notes from the NewFW engineering team.",
      readMore: "Read article",
      back: "Back to blog",
      allPosts: "All posts",
      toc: "Contents",
    },
    footer: {
      tagline: "AI data infrastructure, firmware to tokens.",
      products: "Products",
      solutions: "Solutions",
      contact: "Contact",
      about: "About us",
      blog: "Blog",
      brief: "BDF Datasheet",
      copyright: "Copyright © 2026 NewFW All Rights Reserved.",
    },
  },

  "zh-CN": {
    meta: {
      title: "NewFW — AI 数据基础设施",
      description:
        "NewFW 打造 AI 数据基础设施，从固件到 Token：覆盖 CXL 内存、QLC 存储、集群负载与大模型访问。",
    },
    nav: {
      products: "产品",
      solutions: "解决方案",
      resources: "资源",
      blog: "博客",
      about: "关于",
      contact: "联系我们",
      openMenu: "打开菜单",
      closeMenu: "关闭菜单",
    },
    kind: {
      product: "产品",
      solution: "解决方案",
    },
    productsNav: {
      bdf: "BDF QLC 存储",
      bdfDesc: "面向 AI 数据路径的 QLC 一体机，达到 TLC 级性能",
      cxl: "CXL 内存",
      cxlDesc: "高带宽、低延迟的内存扩展与池化",
      cwp: "云工作负载平台",
      cwpDesc: "工作负载管理、实时监控、自动化测试与性能分析一体的平台",
      token: "Token Service",
      tokenDesc: "统一的大模型 API Key、路由与配额",
      firmware: "固件",
      firmwareDesc: "UEFI UniversalPayload 与基于 USF 的固件栈",
    },
    resources: {
      brief: "BDF 数据手册",
      briefDesc: "QLC 存储一体机数据手册",
      console: "Token Service 控制台",
      consoleDesc: "打开大模型网关",
      blog: "博客",
      blogDesc: "MLPerf 成绩与工程笔记",
    },
    hero: {
      title: "AI 数据基础设施，",
      titleAccent: "从固件到 Token。",
      desc: "QLC 存储、CXL 内存、集群软件与固件，面向大规模 AI 推理与训练。",
      explore: "了解产品",
      flow: [
        { tag: "固件", title: "UEFI / USF 启动栈", note: "Payload · USF · SlimBMC", href: "#firmware" },
        { tag: "CXL", title: "内存扩展与池化", note: "CXL.mem · CXL.io", href: "#cxl" },
        { tag: "BDF", title: "面向 AI 路径的 QLC 存储", note: "118 GB/s · WAF ≈ 1.0", href: "/products/bdf" },
        { tag: "CWP", title: "集群工作负载平台", note: "主机 · GPU · BMC", href: "#cwp" },
        { tag: "Token Service", title: "大模型访问层", note: "OpenAI API · 路由", href: "#token" },
      ],
    },
    grid: {
      kicker: "产品与解决方案",
      title: "我们在构建什么",
      subtitle:
        "产品与解决方案，把 AI 数据基础设施从固件做到 Token。",
      bdfDesc:
        "面向 AI 推理、训练与集群存储的大容量 QLC 一体机：性能达到 TLC 水准，寿命更长，成本也跟得上规模。",
      cxlDesc:
        "CXL Type-3 内存扩展，支持 CXL.mem 与 CXL.io，为机器学习与云计算基础设施提供高带宽、低延迟的内存扩展与池化。",
      cwpDesc: "集工作负载管理、全过程多维度实时监控、自动化测试与性能分析于一体。",
      tokenDesc:
        "通过一套 OpenAI 兼容网关，签发、配额并路由公有模型与私有 GPU 集群的 API Key。",
      firmwareDesc:
        "UEFI UniversalPayload 与基于 USF 的固件栈——从启动到板级管理，开放、可升级。",
    },
    bdf: {
      eyebrow: "BDF · Big Data Foundation",
      subtitle: "面向 AI 数据路径的 QLC 存储一体机",
      desc: "BDF 是面向 AI 推理与训练的软件定义 QLC 存储一体机。主机侧 BDF FTL 写整形把随机写吸收进 Gen5 TLC 缓存，再以顺序流刷入 QLC 底层——从而同时拿到 {{QLC 容量成本}} 与 {{TLC 级性能}}，写放大约 ≈1.0。",
      h1: "面向 AI 推理、训练与集群存储，一套一体机就能覆盖。",
      h2: "以 QLC 的价格，拿到 TLC 级性能——速度快、容量大，不必二选一。",
      h3: "延长 SSD 使用寿命，少故障、少中断，生产可以持续跑下去。",
      button: "了解 BDF",
      imgAlt: "BDF 软件栈：应用、协议、BDF Agent、BDF Storage Core、TLC 缓存与 QLC 底层",
      m1: "顺序读",
      m2: "4K 随机读 IOPS",
      m3: "相对裸 QLC 耐久度提升 5 倍",
      m4: "可用容量，10% OP",
    },
    bdfScenarios: {
      kicker: "适用场景",
      title: "BDF 用在哪里",
      subtitle:
        "一套统一块存储，覆盖 KV Cache、checkpoint、虚拟化与分布式文件系统。",
      i1Tag: "AI 推理",
      i1Title: "KV Cache 卸载",
      i1Desc:
        "1S（4FTL）大块读最高 73.38 GB/s、写最高 25.64 GB/s，缓存段以接近 PCIe 上限的速度加载与落盘，缩短 time-to-first-token。",
      i2Tag: "AI 训练",
      i2Title: "Checkpoint 持久化",
      i2Desc:
        "对称 RAID5F 组在 1S 上可在 4 秒内写入 100 GB checkpoint。突发打满 FTL 带宽后异步下刷 QLC，GPU 循环不必停转。",
      i3Tag: "云原生",
      i3Title: "CSI、NVMe-oF、vhost",
      i3Desc:
        "标准 4K 块设备对接 Kubernetes CSI、VMware / KVM / Hyper-V，以及 NFS 或 MinIO 网关。一台设备同时提供文件、块与对象。",
      i4Tag: "HPC / AI 集群",
      i4Title: "分布式文件系统",
      i4Desc:
        "向 Lustre、BeeGFS 或 JuiceFS 提供 RAID5F 保护的 NVMe 目标盘。耐久性留在一体机上，互连不必为 3 倍副本买单。",
    },
    bdfPage: {
      metaTitle: "BDF QLC 存储 — NewFW",
      metaDescription:
        "BDF 是面向 AI 数据路径的软件定义 QLC 存储一体机——QLC 成本拿到 TLC 级性能，写放大约 ≈1.0。",
      eyebrow: "BDF · Big Data Foundation",
      title: "面向 AI 数据路径的",
      titleAccent: "QLC 存储一体机。",
      lead: "软件定义存储一体机：主机写经 TLC 缓存整形，再以顺序流刷入 QLC 底层——容量成本与性能不再二选一。",
      heroImgAlt: "BDF 2S 24 盘位 QLC 存储一体机",
      download: "下载数据手册",
      contact: "联系我们",
      metrics: [
        { value: "118 GB/s", label: "顺序读" },
        { value: "5.0M", label: "4K 随机读 IOPS" },
        { value: "WAF ≈ 1.0", label: "相对裸 QLC 耐久度提升 5 倍" },
        { value: "1.6 PB", label: "可用容量，10% OP" },
      ],
      overview: {
        kicker: "概览",
        title: "AI 数据基础设施里的存储难题",
        lead: "推理需要 PB 级 KV Cache 与微秒级路径；训练要在不卡住 GPU 循环的前提下落盘多 GB 级 checkpoint。",
        body: "全 TLC 阵列够快，但每 TB 成本过高。裸 QLC 便宜，却栽在随机写上。BDF 用主机侧 {{BDF FTL}} 写整形弥合两端——随机 I/O 先落 Gen5 TLC 缓存，聚合成顺序流再刷 QLC 底层，从而同时拿到 {{QLC 容量成本}} 与 {{TLC 级性能}}，写放大接近 1.0。",
      },
      family: {
        kicker: "配置",
        title: "产品系列",
        subtitle: "1S 与 2S 共用同一软件栈。2S 是 1S 10+2 布局的 NUMA 对称复制。",
        columns: ["", "BDF 1S 12 盘位", "BDF 2S 24 盘位"],
        rows: [
          ["形态", "2U 机架", "2U 机架"],
          ["盘位", "12× NVMe（10 QLC + 2 TLC / 8 QLC + 4 TLC）", "24× NVMe（20 QLC + 4 TLC）"],
          [
            "CPU",
            "Intel® Xeon® 6400 series processors (Sapphire Rapids)\nIntel® Xeon® 6700 series processors (Granite Rapids)",
            "Dual Intel® Xeon® 6400 series processors (Sapphire Rapids)\nDual Intel® Xeon® 6700 series processors (Granite Rapids)",
          ],
          ["内存", "256 → 1024 GB DDR5", "512 → 2048 GB DDR5"],
          ["TLC 缓存", "D7-PS1030 6.4 TB × 2", "D7-PS1030 6.4 TB × 4"],
          [
            "QLC 存储",
            "D5-P5336 × 10（最高 122.88 TB）",
            "D5-P5336 × 20（最高 122.88 TB）",
          ],
          ["原始底层", "307 / 614 / 1229 TB", "614 / 1229 / 2458 TB"],
          ["可用，OP 10%", "~208 / ~416 / ~832 TB", "~416 / ~832 / ~1664 TB"],
        ],
        note: "写密集负载建议 20% OP，以留出 GC 余量并延长盘寿命。",
        driveNote: "D7-PS1030、D5-P5336 为 Solidigm SSD。",
      },
      hardware: {
        kicker: "硬件",
        title: "双层架构，兼具TLC的读写性能与QLC的经济性",
        subtitle: "TLC 缓存承接写路径，QLC 底层提供容量、耐久与成本。",
        points: [
          {
            title: "TLC 缓存层",
            desc: "TLC 缓存承接写路径，极致性能、低延迟，随机与突发 I/O 先落在这里。",
          },
          {
            title: "QLC 底层",
            desc: "高密度 QLC 承载容量。全条带写模式保住耐久度，压低写放大。",
          },
          {
            title: "BDF FTL",
            desc: "BDF FTL 把主机写整形为顺序流量再下刷 QLC——WAF ≈ 1.0，相对裸 QLC 耐久度提升 5 倍。",
          },
          {
            title: "数据保护",
            desc: "缓存层 RAID 1 / RAID 10；底层 RAID5F / RAID6F，全条带写对齐闪存。",
          },
        ],
      },
      software: {
        kicker: "软件",
        title: "四层 SPDK 栈",
        subtitle: "从协议到介质的用户态轮询数据路径。",
        body: "SPDK reactor 绕过内核 I/O 栈。{{BDF FTL}} 把随机写吸入 TLC 缓存，打包成大块顺序写单元再刷 QLC——底层盘只看到符合自身特性的写。管理面包含 Web UI、{{bdf_cli}}，以及覆盖 FTL、RAID 组与 NVMe 盘的 Grafana 遥测。",
      },
      perf: {
        kicker: "性能",
        title: "BDF 一体机性能",
        columns: ["指标", "1S（4 FTL）", "2S（8 FTL）"],
        rows: [
          ["可用（OP 10%）", "166 TB", "332 TB"],
          ["1M 顺序读", "59.2 GB/s", "118 GB/s"],
          ["1M 顺序写", "12.72 GB/s", "25 GB/s"],
          ["4K 随机读", "2.52M IOPS", "5.0M IOPS"],
          ["4K 随机写", "1.27M IOPS", "2.5M IOPS"],
        ],
        insights: [
          "顺序读贴近 QLC 的 PCIe Gen4 上限——8 × 7 GB/s——瓶颈在介质，不在软件。",
          "1S 大块写达 12.72 GB/s：100 GB checkpoint 约 8 秒落盘。",
          "2S 按同一 8+4 布局作 NUMA × 2：顺序读 118 GB/s，4K 随机读 5.0M IOPS。",
        ],
      },
      adv: {
        kicker: "优势",
        title: "QLC 成本，不必做往常那些取舍",
        subtitle: "主机侧 FTL 写整形，对比 MDRAID5 上的裸 QLC。",
        columns: ["指标", "裸 QLC（MDRAID5）", "BDF FTL", "提升"],
        rows: [
          ["4K 随机写", "72.6K IOPS", "514K IOPS", "7.1×"],
          ["128K 随机写", "0.34 GB/s", "6.98 GB/s", "20×"],
          ["顺序写", "1.93 GB/s", "6.94 GB/s", "3.6×"],
          ["写放大", "3–5×", "≈1.0", "降低 3–5×"],
          ["有效盘寿命", "0.56 DWPD 基线", "随 WAF 降低而延长", "5×"],
        ],
      },
    },
    cxl: {
      eyebrow: "CXL 内存 · 平台能力",
      subtitle: "为 AI 服务器扩展内存",
      desc: "CXL Type-3 内存扩展控制器，支持 {{CXL.mem}} 和 {{CXL.io}}，为机器学习、云计算基础设施等数据密集型应用提供高带宽、低延迟的内存扩展与池化。",
      h1: "CXL Type-3 控制器：集成 CXL 控制器、DDR 内存控制器与片上 RISC-V 微处理器",
      h2: "符合 CXL 1.1 / 2.0 / 3.2，支持 CXL.mem 与 CXL.io；基于 PCIe 6.2，最高 64 GT/s（x8 或 2×x4）",
      h3: "符合 JEDEC DDR5，最高 DDR5-8000，支持 UDIMM、RDIMM 与 on-board DRAM",
      button: "联系我们",
      p1Title: "Type-3 内存扩展控制器",
      p1Desc:
        "CXL、DDR 控制器与 RISC-V 核做在同一颗芯片上，带片上 PVT 监测，1211-ball FCBGA 封装。",
      p2Title: "CXL 控制器",
      p2Desc:
        "符合 CXL 1.1、CXL 2.0 及 CXL 3.2 规范，支持 CXL.mem 和 CXL.io。基于 PCIe 6.2 接口，最高 64 GT/s（x8 或 2×x4）。",
      p3Title: "DDR 内存控制器",
      p3Desc:
        "按 JEDEC DDR5 做到 DDR5-8000，UDIMM、RDIMM 与板载颗粒都能接，并针对低功耗做了 I/O 均衡。",
      p4Title: "典型应用",
      p4Desc:
        "用来做内存扩展和池化，可做成 AIC 卡或 EDSFF 模组，也适合内存数据库、云和新兴数据中心负载。",
    },
    cwp: {
      eyebrow: "CWP",
      subtitle: "云工作负载平台",
      desc: "CWP 是一个集 {{工作负载管理}}、全过程多维度 {{实时监控}}、自动化测试和性能分析于一体的强大平台。它让我们能够清晰地观察到测试过程中的 CPU 负载、内存压力、磁盘 I/O 曲线等数十项关键指标。",
      h1: "大规模跨地域集群，在同一控制台管理。",
      h2: "管理各类 workload，从 GPU 验证、SSD 性能测试，到对接 Kubernetes、Slurm 的集群任务。",
      h3: "全过程、多维度实时监控",
      h4: "自动化测试与性能分析，同一平台",
      imgAlt: "NewFW 云工作负载平台架构",
    },
    cwpUse: {
      kicker: "适用场景",
      title: "CWP 为谁而建",
      subtitle: "同一平台支撑集群负载、产线测试、AI 评测与日常集群可见性。",
      i1Tag: "集群",
      i1Title: "工作负载管理",
      i1Desc:
        "把任务写成 Recipe，挂上 tools / datasets / models / containers，在集群上跑出完整历史。",
      i2Tag: "工厂",
      i2Title: "自动化测试",
      i2Desc:
        "把板卡与元器件检查变成可重复的运行——尽早发现硬件问题，通过率轨迹清晰可查。",
      i3Tag: "分析",
      i3Title: "性能分析",
      i3Desc:
        "看清一次运行为何卡住：步骤与 CPU、内存压力、磁盘 I/O、GPU 信号对齐在同一时间线。",
      i4Tag: "观测",
      i4Title: "全过程可见",
      i4Desc:
        "主机、GPU、磁盘与 BMC 带外指标与负载并排——含 BKBC 主机配置上下文。",
    },
    cwpPage: {
      metaTitle: "CWP — 云工作负载平台 — NewFW",
      metaDescription:
        "CWP 管理上万台主机，用 Recipe 编排负载，把分步日志与主机 / GPU / SSD / BMC 遥测留在同一条轨迹上，并用 BKBC 基准系统保持集群环境一致。",
      eyebrow: "CWP · 云工作负载平台",
      title: "集群上的每一次负载，",
      titleAccent: "同一套环境，全过程看得见。",
      lead: "CWP 管理上万台主机。Agent 自动安装与更新，Recipe 按策略下发。每一步留下执行日志，并与测试窗口内的主机、GPU、SSD 负载对齐——OS 不可用时还有 BMC。BKBC 是基准系统：装上即可，驱动和软件已经在里面。",
      contact: "联系我们",
      howCta: "看四层怎么叠",
      hero: {
        imgAlt: "CWP 控制台：集群主机、Recipe 运行、主机 / GPU / 磁盘实时监控",
        caption: "一个控制台——集群 Agent、Recipe 运行、主机 / GPU / SSD / BMC 遥测。",
        panes: [
          { tag: "集群", title: "上万台主机" },
          { tag: "Recipe", title: "工具 · 容器 · 数据集 · 模型" },
          { tag: "观测", title: "步骤 + 主机 / GPU / SSD + BMC" },
          { tag: "BKBC", title: "基准系统" },
        ],
      },
      facts: [
        { value: "上万台", label: "同一集群内的主机" },
        { value: "Agent", label: "自动安装、更新、下发" },
        { value: "Recipe", label: "命令 · 工具 · 容器 · 数据集 · 模型" },
        { value: "BKBC", label: "基准系统，驱动与软件预装" },
      ],
      overview: {
        kicker: "现状",
        title: "任务、机器、环境还是三件事",
        lead:
          "脚本在一台机器上，Grafana 在另一处，第三台 CUDA 又不一样。结果不可比，失败也没有轨迹。",
        body:
          "CWP 把这三件事收口。{{BKBC}} 让操作系统一致。{{Agent}} 覆盖集群——自动安装、自动更新、自动下发。{{Recipe}} 就是任务，不是 wiki 页面。{{全链路遥测}} 就是证据：分步日志、测试窗口内的主机 / GPU / SSD 负载，以及 BMC 带外。",
      },
      how: {
        kicker: "怎么叠起来",
        title: "先基准系统，再集群，再运行",
        subtitle: "四层。少一层，其余几层就不可比。",
        body:
          "先装 {{BKBC}}，每台主机从同一套操作系统起步。用会自动安装、更新、下发的 {{Agent}} 覆盖集群。把负载写成 {{Recipe}}。留下 {{全链路轨迹}}，下一次运行是对比，不是重建现场。",
        steps: [
          {
            title: "安装 BKBC",
            desc: "基准系统镜像：负载所需的驱动和软件已经在里面。",
          },
          {
            title: "覆盖集群",
            desc: "上万台主机上 Agent 自动安装与更新，再按策略下发工作负载。",
          },
          {
            title: "编写 Recipe",
            desc: "系统命令打底；复杂作业再挂上工具、容器、数据集和模型。",
          },
          {
            title: "留下轨迹",
            desc: "分步日志，同一测试窗口内的主机 / GPU / SSD 负载，以及 BMC 带外。",
          },
        ],
        diagramAlt: "CWP 四层：从 BKBC 基准系统到全链路遥测",
      },
      pillars: {
        kicker: "CWP 做什么",
        title: "四件事，一个产品",
        subtitle: "不是四套模块拼在一起——四层都在，集群才站得住。",
        problem: "现状",
        items: [
          {
            id: "cluster",
            tag: "集群",
            title: "管理上万台主机",
            problem:
              "一台台 SSH。插件没人升级。工作负载只到了当时碰巧登录过的那几台机器。",
            body: "CWP 是集群控制台。Agent {{自行下载与更新}}。工作负载按 GPU 型号、地域或 Agent ID {{自动下发}} 到匹配的主机。",
            points: [
              "上万台主机，一个控制台",
              "Agent 自动安装与自动更新",
              "按策略下发，而不是一台台 SSH",
            ],
            diagramAlt: "CWP 控制台向主机 Agent 下发任务",
          },
          {
            id: "workload",
            tag: "负载",
            title: "用 Recipe 编排作业",
            problem:
              "复杂作业还在 shell 和「上次改过这台机器的人」手里。两台主机跑着同一任务的两个版本。",
            body: "Recipe 是 {{可版本化的多步骤}} 作业。从 {{系统命令}} 开始。作业不只是脚本时，再从资源中心挂上 {{工具、容器、数据集和模型}}。下发之后，每次运行都进入历史。",
            points: [
              "系统命令是底",
              "复杂作业用工具、容器、数据集和模型拼出来",
              "可版本化的 Recipe，带完整执行历史",
            ],
            diagramAlt: "CWP Recipe 从编写到主机执行",
          },
          {
            id: "observe",
            tag: "观测",
            title: "全链路日志与遥测",
            problem:
              "步骤日志在一处，Grafana 在另一处，BMC 再开一个控制台。三套时钟对不齐。",
            body: "CWP 收集 {{分步执行日志}}，并在同一 {{测试窗口}} 内记录主机、GPU、SSD 负载。{{BMC / Redfish}} 在操作系统不可用时仍然在。",
            points: [
              "每个任务和子任务都有分步日志",
              "主机、GPU、SSD 负载对齐测试时间窗",
              "不依赖操作系统的 BMC 带外",
            ],
            diagramAlt: "CWP 分步日志与主机、GPU、SSD、BMC 遥测并排",
          },
          {
            id: "bkbc",
            tag: "BKBC",
            title: "安装基准系统",
            problem:
              "同一份 Recipe，CUDA、驱动、工具链却不一样——结果不可比，集群也没法复现。",
            body: "BKBC（也叫 BKC）是 {{Best Known Configuration}}——一套基准系统镜像。用户 {{通过 BKBC 安装系统}}，负载所需的驱动和软件已经在里面。不用逐台配置，集群环境保持一致。",
            points: [
              "基准系统镜像，不是一份配置快照",
              "驱动和软件预装",
              "每台主机上的 Recipe 跑在同一套环境里",
            ],
            diagramAlt: "BKBC 基准系统镜像安装到集群主机",
          },
        ],
      },
      architecture: {
        kicker: "架构",
        title: "控制台、Agent、硬件",
        subtitle: "从集群控制台直达机器——包括 BMC 带外。",
        body: "控制台承载 Recipe、资源、故障、日志与仪表盘。平台服务到达每台 {{主机 Agent}}——安装、更新、下发。Agent 执行作业并回传主机 / GPU / SSD / BMC 信号。{{BKBC}} 是主机怎么起步：一套基准操作系统，而不是 wiki 里的一笔。",
        layers: [
          "控制台 — Recipe、集群 Agent、实时监控",
          "平台服务 — Agent 生命周期、资产、BKBC、下发",
          "主机 Agent — 执行、采集、日志、指标",
          "硬件 — GPU、SSD、BMC 带外、平台",
        ],
        diagramAlt: "CWP 架构：控制台、服务、Agent、硬件",
      },
      compare: {
        kicker: "不必再拼凑",
        title: "CWP 在流程里替换什么",
        subtitle: "同一套集群，不必事后把四个控制台拼回去。",
        columns: ["", "脚本 + SSH + Grafana", "CWP"],
        rows: [
          ["主机", "一台台 SSH", "Agent 自动安装 / 更新，上万台主机"],
          ["任务", "Shell 和 wiki", "可版本化 Recipe + 工具 / 容器 / 数据集 / 模型"],
          ["环境", "每台机器不一样", "BKBC 基准系统"],
          ["日志", "步骤日志或 Grafana，对不齐", "分步日志 + 测试窗口内主机 / GPU / SSD"],
          ["带外", "再开一个 BMC 控制台", "BMC / Redfish 在同一条轨迹上"],
          ["失败之后", "从日志里重建现场", "步骤、信号、日志已经在一起"],
        ],
      },
      proof: {
        kicker: "实际用法",
        title: "NewFW MLPerf Storage 测试怎么跑",
        body: "每一项 MLPerf Storage 基准都被做成 CWP Recipe——由主机 Agent 下发、实时监控，日志与遥测进入提交给 MLCommons 的审计轨迹。",
        link: "阅读方法论",
        href: "/blog/mlperf-storage-v2",
      },
      close: {
        title: "把下一批集群任务放到 CWP 上",
        body: "欢迎交流试用——集群负载、产线测试，或 AI 基准。",
        contact: "联系我们",
      },
    },
    token: {
      eyebrow: "Token Service",
      subtitle: "签发、配额并路由每一枚 LLM Token",
      desc: "Token Service 是 NewFW 的大模型访问层。一套 {{OpenAI 兼容 API}} 同时接入 50+ 公有模型以及你的私有 vLLM / Ollama / GPU 集群。按组、配额与过期时间签发 Key；简单任务走本地，重推理走云端；敏感 prompt 留在边界内。",
      h1: "批量建 Key、设上限、过期并审计用量",
      h2: "混合路由——公有 GPT / Claude / Gemini / DeepSeek / GLM 加上本地算力",
      h3: "Claude Code、Cursor、Cline、OpenCode 等 agent 共用一个网关",
      h4: "钱包、模型市场，以及面向 99.9% 可用的故障切换",
      button: "打开控制台",
      p1Title: "一把钥匙，多种模型",
      p1Desc: "应用只保留一个 SDK 与端点，由 Token Service 把 Key 映射到模型组。",
      p2Title: "云端 + 私有 GPU",
      p2Desc:
        "BYOC：接入 vLLM、Ollama 或 TGI。供应商抖动时故障切换；机密请求钉在本地模型。",
      p3Title: "面向 Agent",
      p3Desc:
        "SeaChart Code 挡在编程 agent 后面，企业把 Token 花费与数据路径留在策略内。",
      p4Title: "运维看得见",
      p4Desc: "仪表盘、查询日志、钱包与通道健康——Token 是库存，不是表格。",
    },
    firmware: {
      subtitle: "UEFI UniversalPayload 固件",
      desc: "NewFW UEFI UniversalPayload 固件基于开源项目 {{Linux as the firmware}}，集成 {{u-root}} 用 Go 实现的标准 Linux 工具，以及 {{USF UniversalPayload}}，用于快速验证与部署。同一套固件覆盖云固件与 SlimBMC 板级管理。",
      imgAlt: "NewFW 基于 USF 的固件架构总览",
    },
    about: {
      kicker: "公司",
      title: "关于 NewFW",
      lead: "NewFW 打造从固件到 Token 的 AI 数据基础设施：主机与 BMC 固件、CXL 内存、面向 AI 数据路径的 QLC 存储、集群负载控制台，以及大模型 Token 网关。",
      talk: "关于 BDF、固件或 Token Service，欢迎联系：",
    },
    blog: {
      kicker: "洞察",
      title: "博客",
      subtitle: "MLPerf Storage 成绩、CWP 方法论，以及 NewFW 工程团队的笔记。",
      readMore: "阅读全文",
      back: "返回博客",
      allPosts: "全部文章",
      toc: "目录",
    },
    footer: {
      tagline: "AI 数据基础设施，从固件到 Token。",
      products: "产品",
      solutions: "解决方案",
      contact: "联系",
      about: "关于我们",
      blog: "博客",
      brief: "BDF 数据手册",
      copyright: "Copyright © 2026 NewFW 保留所有权利。",
    },
  },

  "zh-TW": {
    meta: {
      title: "NewFW — AI 數據基礎設施",
      description:
        "NewFW 打造 AI 數據基礎設施，從韌體到 Token：涵蓋 CXL 記憶體、QLC 儲存、叢集負載與大型語言模型存取。",
    },
    nav: {
      products: "產品",
      solutions: "解決方案",
      resources: "資源",
      blog: "部落格",
      about: "關於",
      contact: "聯絡我們",
      openMenu: "開啟選單",
      closeMenu: "關閉選單",
    },
    kind: {
      product: "產品",
      solution: "解決方案",
    },
    productsNav: {
      bdf: "BDF QLC 儲存",
      bdfDesc: "面向 AI 資料路徑的 QLC 一體機，達到 TLC 級效能",
      cxl: "CXL 記憶體",
      cxlDesc: "高頻寬、低延遲的記憶體擴充與池化",
      cwp: "雲端工作負載平台",
      cwpDesc: "工作負載管理、即時監控、自動化測試與效能分析一體的平台",
      token: "Token Service",
      tokenDesc: "統一的大型語言模型 API Key、路由與配額",
      firmware: "韌體",
      firmwareDesc: "UEFI UniversalPayload 與基於 USF 的韌體堆疊",
    },
    resources: {
      brief: "BDF 資料手冊",
      briefDesc: "QLC 儲存一體機資料手冊",
      console: "Token Service 主控台",
      consoleDesc: "開啟大型語言模型閘道",
      blog: "部落格",
      blogDesc: "MLPerf 成績與工程筆記",
    },
    hero: {
      title: "AI 數據基礎設施，",
      titleAccent: "從韌體到 Token。",
      desc: "QLC 儲存、CXL 記憶體、叢集軟體與韌體，面向大規模 AI 推論與訓練。",
      explore: "了解產品",
      flow: [
        { tag: "韌體", title: "UEFI / USF 啟動堆疊", note: "Payload · USF · SlimBMC", href: "#firmware" },
        { tag: "CXL", title: "記憶體擴充與池化", note: "CXL.mem · CXL.io", href: "#cxl" },
        { tag: "BDF", title: "面向 AI 路徑的 QLC 儲存", note: "118 GB/s · WAF ≈ 1.0", href: "/products/bdf" },
        { tag: "CWP", title: "叢集工作負載平台", note: "主機 · GPU · BMC", href: "#cwp" },
        { tag: "Token Service", title: "大型語言模型存取層", note: "OpenAI API · 路由", href: "#token" },
      ],
    },
    grid: {
      kicker: "產品與解決方案",
      title: "我們在建構什麼",
      subtitle:
        "產品與解決方案，把 AI 數據基礎設施從韌體做到 Token。",
      bdfDesc:
        "面向 AI 推論、訓練與叢集儲存的大容量 QLC 一體機：效能達到 TLC 水準，壽命更長，成本也跟得上規模。",
      cxlDesc:
        "CXL Type-3 記憶體擴充，支援 CXL.mem 與 CXL.io，為機器學習與雲端運算基礎設施提供高頻寬、低延遲的記憶體擴充與池化。",
      cwpDesc: "集工作負載管理、全過程多維度即時監控、自動化測試與效能分析於一體。",
      tokenDesc:
        "透過一套 OpenAI 相容閘道，簽發、配額並路由公有模型與私有 GPU 叢集的 API Key。",
      firmwareDesc:
        "UEFI UniversalPayload 與基於 USF 的韌體堆疊——從啟動到板級管理，開放、可升級。",
    },
    bdf: {
      eyebrow: "BDF · Big Data Foundation",
      subtitle: "面向 AI 資料路徑的 QLC 儲存一體機",
      desc: "BDF 是面向 AI 推論與訓練的軟體定義 QLC 儲存一體機。主機側 BDF FTL 寫入整形把隨機寫吸收進 Gen5 TLC 快取，再以循序流刷入 QLC 底層——從而同時拿到 {{QLC 容量成本}} 與 {{TLC 級效能}}，寫入放大約 ≈1.0。",
      h1: "面向 AI 推論、訓練與叢集儲存，一套一體機就能涵蓋。",
      h2: "以 QLC 的價格，拿到 TLC 級效能——速度快、容量大，不必二選一。",
      h3: "延長 SSD 使用壽命，少故障、少中斷，生產可以持續跑下去。",
      button: "了解 BDF",
      imgAlt: "BDF 軟體堆疊：應用、協定、BDF Agent、BDF Storage Core、TLC 快取與 QLC 底層",
      m1: "循序讀",
      m2: "4K 隨機讀 IOPS",
      m3: "相對裸 QLC 耐久度提升 5 倍",
      m4: "可用容量，10% OP",
    },
    bdfScenarios: {
      kicker: "適用場景",
      title: "BDF 用在哪裡",
      subtitle:
        "一套統一區塊儲存，涵蓋 KV Cache、checkpoint、虛擬化與分散式檔案系統。",
      i1Tag: "AI 推論",
      i1Title: "KV Cache 卸載",
      i1Desc:
        "1S（4FTL）大塊讀最高 73.38 GB/s、寫最高 25.64 GB/s，快取段以接近 PCIe 上限的速度載入與落盤，縮短 time-to-first-token。",
      i2Tag: "AI 訓練",
      i2Title: "Checkpoint 持久化",
      i2Desc:
        "對稱 RAID5F 組在 1S 上可在 4 秒內寫入 100 GB checkpoint。突發打滿 FTL 頻寬後非同步下刷 QLC，GPU 迴圈不必停轉。",
      i3Tag: "雲原生",
      i3Title: "CSI、NVMe-oF、vhost",
      i3Desc:
        "標準 4K 區塊裝置對接 Kubernetes CSI、VMware / KVM / Hyper-V，以及 NFS 或 MinIO 閘道。一台設備同時提供檔案、區塊與物件。",
      i4Tag: "HPC / AI 叢集",
      i4Title: "分散式檔案系統",
      i4Desc:
        "向 Lustre、BeeGFS 或 JuiceFS 提供 RAID5F 保護的 NVMe 目標碟。耐久性留在一體機上，互連不必為 3 倍副本買單。",
    },
    bdfPage: {
      metaTitle: "BDF QLC 儲存 — NewFW",
      metaDescription:
        "BDF 是面向 AI 資料路徑的軟體定義 QLC 儲存一體機——QLC 成本拿到 TLC 級效能，寫入放大約 ≈1.0。",
      eyebrow: "BDF · Big Data Foundation",
      title: "面向 AI 資料路徑的",
      titleAccent: "QLC 儲存一體機。",
      lead: "軟體定義儲存一體機：主機寫經 TLC 快取整形，再以循序流刷入 QLC 底層——容量成本與效能不再二選一。",
      heroImgAlt: "BDF 2S 24 碟位 QLC 儲存一體機",
      download: "下載資料手冊",
      contact: "聯絡我們",
      metrics: [
        { value: "118 GB/s", label: "循序讀" },
        { value: "5.0M", label: "4K 隨機讀 IOPS" },
        { value: "WAF ≈ 1.0", label: "相對裸 QLC 耐久度提升 5 倍" },
        { value: "1.6 PB", label: "可用容量，10% OP" },
      ],
      overview: {
        kicker: "概覽",
        title: "AI 數據基礎設施裡的儲存難題",
        lead: "推論需要 PB 級 KV Cache 與微秒級路徑；訓練要在不卡住 GPU 迴圈的前提下落盤多 GB 級 checkpoint。",
        body: "全 TLC 陣列夠快，但每 TB 成本過高。裸 QLC 便宜，卻栽在隨機寫上。BDF 用主機側 {{BDF FTL}} 寫入整形彌合兩端——隨機 I/O 先落 Gen5 TLC 快取，聚合成循序流再刷 QLC 底層，從而同時拿到 {{QLC 容量成本}} 與 {{TLC 級效能}}，寫入放大接近 1.0。",
      },
      family: {
        kicker: "配置",
        title: "產品系列",
        subtitle: "1S 與 2S 共用同一軟體堆疊。2S 是 1S 10+2 佈局的 NUMA 對稱複製。",
        columns: ["", "BDF 1S 12 碟位", "BDF 2S 24 碟位"],
        rows: [
          ["形態", "2U 機架", "2U 機架"],
          ["碟位", "12× NVMe（10 QLC + 2 TLC / 8 QLC + 4 TLC）", "24× NVMe（20 QLC + 4 TLC）"],
          [
            "CPU",
            "Intel® Xeon® 6400 series processors (Sapphire Rapids)\nIntel® Xeon® 6700 series processors (Granite Rapids)",
            "Dual Intel® Xeon® 6400 series processors (Sapphire Rapids)\nDual Intel® Xeon® 6700 series processors (Granite Rapids)",
          ],
          ["記憶體", "256 → 1024 GB DDR5", "512 → 2048 GB DDR5"],
          ["TLC 快取", "D7-PS1030 6.4 TB × 2", "D7-PS1030 6.4 TB × 4"],
          [
            "QLC 儲存",
            "D5-P5336 × 10（最高 122.88 TB）",
            "D5-P5336 × 20（最高 122.88 TB）",
          ],
          ["原始底層", "307 / 614 / 1229 TB", "614 / 1229 / 2458 TB"],
          ["可用，OP 10%", "~208 / ~416 / ~832 TB", "~416 / ~832 / ~1664 TB"],
        ],
        note: "寫入密集負載建議 20% OP，以留出 GC 餘量並延長碟壽命。",
        driveNote: "D7-PS1030、D5-P5336 為 Solidigm SSD。",
      },
      hardware: {
        kicker: "硬體",
        title: "雙層架構，兼具TLC的讀寫效能與QLC的經濟性",
        subtitle: "TLC 快取承接寫入路徑，QLC 底層提供容量、耐久與成本。",
        points: [
          {
            title: "TLC 快取層",
            desc: "TLC 快取承接寫入路徑，極致效能、低延遲，隨機與突發 I/O 先落在這裡。",
          },
          {
            title: "QLC 底層",
            desc: "高密度 QLC 承載容量。全條帶寫入模式保住耐久度，壓低寫入放大。",
          },
          {
            title: "BDF FTL",
            desc: "BDF FTL 把主機寫整形為循序流量再下刷 QLC——WAF ≈ 1.0，相對裸 QLC 耐久度提升 5 倍。",
          },
          {
            title: "資料保護",
            desc: "快取層 RAID 1 / RAID 10；底層 RAID5F / RAID6F，全條帶寫入對齊閃存。",
          },
        ],
      },
      software: {
        kicker: "軟體",
        title: "四層 SPDK 堆疊",
        subtitle: "從協定到介質的使用者態輪詢資料路徑。",
        body: "SPDK reactor 繞過核心 I/O 堆疊。{{BDF FTL}} 把隨機寫吸入 TLC 快取，打包成大塊循序寫入單元再刷 QLC——底層碟只看到符合自身特性的寫。管理面包含 Web UI、{{bdf_cli}}，以及涵蓋 FTL、RAID 組與 NVMe 碟的 Grafana 遙測。",
      },
      perf: {
        kicker: "效能",
        title: "BDF 一體機效能",
        columns: ["指標", "1S（4 FTL）", "2S（8 FTL）"],
        rows: [
          ["可用（OP 10%）", "166 TB", "332 TB"],
          ["1M 循序讀", "59.2 GB/s", "118 GB/s"],
          ["1M 循序寫", "12.72 GB/s", "25 GB/s"],
          ["4K 隨機讀", "2.52M IOPS", "5.0M IOPS"],
          ["4K 隨機寫", "1.27M IOPS", "2.5M IOPS"],
        ],
        insights: [
          "循序讀貼近 QLC 的 PCIe Gen4 上限——8 × 7 GB/s——瓶頸在介質，不在軟體。",
          "1S 大塊寫達 12.72 GB/s：100 GB checkpoint 約 8 秒落盤。",
          "2S 按同一 8+4 佈局作 NUMA × 2：循序讀 118 GB/s，4K 隨機讀 5.0M IOPS。",
        ],
      },
      adv: {
        kicker: "優勢",
        title: "QLC 成本，不必做往常那些取捨",
        subtitle: "主機側 FTL 寫入整形，對比 MDRAID5 上的裸 QLC。",
        columns: ["指標", "裸 QLC（MDRAID5）", "BDF FTL", "提升"],
        rows: [
          ["4K 隨機寫", "72.6K IOPS", "514K IOPS", "7.1×"],
          ["128K 隨機寫", "0.34 GB/s", "6.98 GB/s", "20×"],
          ["循序寫", "1.93 GB/s", "6.94 GB/s", "3.6×"],
          ["寫入放大", "3–5×", "≈1.0", "降低 3–5×"],
          ["有效碟壽命", "0.56 DWPD 基線", "隨 WAF 降低而延長", "5×"],
        ],
      },
    },
    cxl: {
      eyebrow: "CXL 記憶體 · 平台能力",
      subtitle: "為 AI 伺服器擴充記憶體",
      desc: "CXL Type-3 記憶體擴充控制器，支援 {{CXL.mem}} 與 {{CXL.io}}，為機器學習、雲端運算基礎設施等資料密集型應用提供高頻寬、低延遲的記憶體擴充與池化。",
      h1: "CXL Type-3 控制器：整合 CXL 控制器、DDR 記憶體控制器與片上 RISC-V 微處理器",
      h2: "符合 CXL 1.1 / 2.0 / 3.2，支援 CXL.mem 與 CXL.io；基於 PCIe 6.2，最高 64 GT/s（x8 或 2×x4）",
      h3: "符合 JEDEC DDR5，最高 DDR5-8000，支援 UDIMM、RDIMM 與 on-board DRAM",
      button: "聯絡我們",
      p1Title: "Type-3 記憶體擴充控制器",
      p1Desc:
        "CXL、DDR 控制器與 RISC-V 核做在同一顆晶片上，帶片上 PVT 監測，1211-ball FCBGA 封裝。",
      p2Title: "CXL 控制器",
      p2Desc:
        "符合 CXL 1.1、CXL 2.0 及 CXL 3.2 規範，支援 CXL.mem 和 CXL.io。基於 PCIe 6.2 介面，最高 64 GT/s（x8 或 2×x4）。",
      p3Title: "DDR 記憶體控制器",
      p3Desc:
        "依 JEDEC DDR5 做到 DDR5-8000，UDIMM、RDIMM 與板載顆粒都能接，並針對低功耗做了 I/O 均衡。",
      p4Title: "典型應用",
      p4Desc:
        "用來做記憶體擴充與池化，可做成 AIC 卡或 EDSFF 模組，也適合記憶體資料庫、雲端與新興資料中心負載。",
    },
    cwp: {
      eyebrow: "CWP",
      subtitle: "雲端工作負載平台",
      desc: "CWP 是一個集 {{工作負載管理}}、全過程多維度 {{即時監控}}、自動化測試和效能分析於一體的強大平台。它讓我們能夠清晰地觀察到測試過程中的 CPU 負載、記憶體壓力、磁碟 I/O 曲線等數十項關鍵指標。",
      h1: "大規模跨地域叢集，在同一主控台管理。",
      h2: "管理各類 workload，從 GPU 驗證、SSD 效能測試，到對接 Kubernetes、Slurm 的叢集任務。",
      h3: "全過程、多維度即時監控",
      h4: "自動化測試與效能分析，同一平台",
      imgAlt: "NewFW 雲端工作負載平台架構",
    },
    cwpUse: {
      kicker: "適用場景",
      title: "CWP 為誰而建",
      subtitle: "同一平台支撐叢集負載、產線測試、AI 評測與日常叢集可見性。",
      i1Tag: "叢集",
      i1Title: "工作負載管理",
      i1Desc:
        "把任務寫成 Recipe，掛上 tools / datasets / models / containers，在叢集上跑出完整歷史。",
      i2Tag: "工廠",
      i2Title: "自動化測試",
      i2Desc:
        "把板卡與元件檢查變成可重複的執行——儘早發現硬體問題，通過率軌跡清晰可查。",
      i3Tag: "分析",
      i3Title: "效能分析",
      i3Desc:
        "看清一次執行為何卡住：步驟與 CPU、記憶體壓力、磁碟 I/O、GPU 訊號對齊在同一時間線。",
      i4Tag: "觀測",
      i4Title: "全過程可見",
      i4Desc:
        "主機、GPU、磁碟與 BMC 帶外指標與負載並排——含 BKBC 主機組態上下文。",
    },
    cwpPage: {
      metaTitle: "CWP — 雲端工作負載平台 — NewFW",
      metaDescription:
        "CWP 管理上萬台主機，用 Recipe 編排負載，把分步日誌與主機 / GPU / SSD / BMC 遙測留在同一條軌跡上，並用 BKBC 基準系統保持叢集環境一致。",
      eyebrow: "CWP · 雲端工作負載平台",
      title: "叢集上的每一次負載，",
      titleAccent: "同一套環境，全過程看得見。",
      lead: "CWP 管理上萬台主機。Agent 自動安裝與更新，Recipe 按策略下發。每一步留下執行日誌，並與測試視窗內的主機、GPU、SSD 負載對齊——OS 不可用時還有 BMC。BKBC 是基準系統：裝上即可，驅動和軟體已經在裡面。",
      contact: "聯絡我們",
      howCta: "看四層怎麼疊",
      hero: {
        imgAlt: "CWP 主控台：叢集主機、Recipe 執行、主機 / GPU / 磁碟即時監控",
        caption: "一個主控台——叢集 Agent、Recipe 執行、主機 / GPU / SSD / BMC 遙測。",
        panes: [
          { tag: "叢集", title: "上萬台主機" },
          { tag: "Recipe", title: "工具 · 容器 · 資料集 · 模型" },
          { tag: "觀測", title: "步驟 + 主機 / GPU / SSD + BMC" },
          { tag: "BKBC", title: "基準系統" },
        ],
      },
      facts: [
        { value: "上萬台", label: "同一叢集內的主機" },
        { value: "Agent", label: "自動安裝、更新、下發" },
        { value: "Recipe", label: "命令 · 工具 · 容器 · 資料集 · 模型" },
        { value: "BKBC", label: "基準系統，驅動與軟體預裝" },
      ],
      overview: {
        kicker: "現況",
        title: "任務、機器、環境還是三件事",
        lead:
          "腳本在一台機器上，Grafana 在另一處，第三台 CUDA 又不一樣。結果不可比，失敗也沒有軌跡。",
        body:
          "CWP 把這三件事收口。{{BKBC}} 讓作業系統一致。{{Agent}} 覆蓋叢集——自動安裝、自動更新、自動下發。{{Recipe}} 就是任務，不是 wiki 頁面。{{全鏈路遙測}} 就是證據：分步日誌、測試視窗內的主機 / GPU / SSD 負載，以及 BMC 帶外。",
      },
      how: {
        kicker: "怎麼疊起來",
        title: "先基準系統，再叢集，再執行",
        subtitle: "四層。少一層，其餘幾層就不可比。",
        body:
          "先裝 {{BKBC}}，每台主機從同一套作業系統起步。用會自動安裝、更新、下發的 {{Agent}} 覆蓋叢集。把負載寫成 {{Recipe}}。留下 {{全鏈路軌跡}}，下一次執行是對比，不是重建現場。",
        steps: [
          {
            title: "安裝 BKBC",
            desc: "基準系統映像：負載所需的驅動和軟體已經在裡面。",
          },
          {
            title: "覆蓋叢集",
            desc: "上萬台主機上 Agent 自動安裝與更新，再按策略下發工作負載。",
          },
          {
            title: "編寫 Recipe",
            desc: "系統命令打底；複雜作業再掛上工具、容器、資料集和模型。",
          },
          {
            title: "留下軌跡",
            desc: "分步日誌，同一測試視窗內的主機 / GPU / SSD 負載，以及 BMC 帶外。",
          },
        ],
        diagramAlt: "CWP 四層：從 BKBC 基準系統到全鏈路遙測",
      },
      pillars: {
        kicker: "CWP 做什麼",
        title: "四件事，一個產品",
        subtitle: "不是四套模組拼在一起——四層都在，叢集才站得住。",
        problem: "現況",
        items: [
          {
            id: "cluster",
            tag: "叢集",
            title: "管理上萬台主機",
            problem:
              "一台台 SSH。外掛沒人升級。工作負載只到了當時碰巧登入過的那幾台機器。",
            body: "CWP 是叢集主控台。Agent {{自行下載與更新}}。工作負載按 GPU 型號、地域或 Agent ID {{自動下發}} 到匹配的主機。",
            points: [
              "上萬台主機，一個主控台",
              "Agent 自動安裝與自動更新",
              "按策略下發，而不是一台台 SSH",
            ],
            diagramAlt: "CWP 主控台向主機 Agent 下發任務",
          },
          {
            id: "workload",
            tag: "負載",
            title: "用 Recipe 編排作業",
            problem:
              "複雜作業還在 shell 和「上次改過這台機器的人」手裡。兩台主機跑著同一任務的兩個版本。",
            body: "Recipe 是 {{可版本化的多步驟}} 作業。從 {{系統命令}} 開始。作業不只是腳本時，再從資源中心掛上 {{工具、容器、資料集和模型}}。下發之後，每次執行都進入歷史。",
            points: [
              "系統命令是底",
              "複雜作業用工具、容器、資料集和模型拼出來",
              "可版本化的 Recipe，帶完整執行歷史",
            ],
            diagramAlt: "CWP Recipe 從編寫到主機執行",
          },
          {
            id: "observe",
            tag: "觀測",
            title: "全鏈路日誌與遙測",
            problem:
              "步驟日誌在一處，Grafana 在另一處，BMC 再開一個主控台。三套時鐘對不齊。",
            body: "CWP 收集 {{分步執行日誌}}，並在同一 {{測試視窗}} 內記錄主機、GPU、SSD 負載。{{BMC / Redfish}} 在作業系統不可用時仍然在。",
            points: [
              "每個任務和子任務都有分步日誌",
              "主機、GPU、SSD 負載對齊測試時間窗",
              "不依賴作業系統的 BMC 帶外",
            ],
            diagramAlt: "CWP 分步日誌與主機、GPU、SSD、BMC 遙測並排",
          },
          {
            id: "bkbc",
            tag: "BKBC",
            title: "安裝基準系統",
            problem:
              "同一份 Recipe，CUDA、驅動、工具鏈卻不一樣——結果不可比，叢集也沒法重現。",
            body: "BKBC（也叫 BKC）是 {{Best Known Configuration}}——一套基準系統映像。使用者 {{透過 BKBC 安裝系統}}，負載所需的驅動和軟體已經在裡面。不用逐台設定，叢集環境保持一致。",
            points: [
              "基準系統映像，不是一份組態快照",
              "驅動和軟體預裝",
              "每台主機上的 Recipe 跑在同一套環境裡",
            ],
            diagramAlt: "BKBC 基準系統映像安裝到叢集主機",
          },
        ],
      },
      architecture: {
        kicker: "架構",
        title: "主控台、Agent、硬體",
        subtitle: "從叢集主控台直達機器——包括 BMC 帶外。",
        body: "主控台承載 Recipe、資源、故障、日誌與儀表板。平台服務到達每台 {{主機 Agent}}——安裝、更新、下發。Agent 執行作業並回傳主機 / GPU / SSD / BMC 訊號。{{BKBC}} 是主機怎麼起步：一套基準作業系統，而不是 wiki 裡的一筆。",
        layers: [
          "主控台 — Recipe、叢集 Agent、即時監控",
          "平台服務 — Agent 生命週期、資產、BKBC、下發",
          "主機 Agent — 執行、採集、日誌、指標",
          "硬體 — GPU、SSD、BMC 帶外、平台",
        ],
        diagramAlt: "CWP 架構：主控台、服務、Agent、硬體",
      },
      compare: {
        kicker: "不必再拼湊",
        title: "CWP 在流程裡替換什麼",
        subtitle: "同一套叢集，不必事後把四個主控台拼回去。",
        columns: ["", "腳本 + SSH + Grafana", "CWP"],
        rows: [
          ["主機", "一台台 SSH", "Agent 自動安裝 / 更新，上萬台主機"],
          ["任務", "Shell 和 wiki", "可版本化 Recipe + 工具 / 容器 / 資料集 / 模型"],
          ["環境", "每台機器不一樣", "BKBC 基準系統"],
          ["日誌", "步驟日誌或 Grafana，對不齊", "分步日誌 + 測試視窗內主機 / GPU / SSD"],
          ["帶外", "再開一個 BMC 主控台", "BMC / Redfish 在同一條軌跡上"],
          ["失敗之後", "從日誌裡重建現場", "步驟、訊號、日誌已經在一起"],
        ],
      },
      proof: {
        kicker: "實際用法",
        title: "NewFW MLPerf Storage 測試怎麼跑",
        body: "每一項 MLPerf Storage 基準都被做成 CWP Recipe——由主機 Agent 下發、即時監控，日誌與遙測進入提交給 MLCommons 的稽核軌跡。",
        link: "閱讀方法論",
        href: "/blog/mlperf-storage-v3",
      },
      close: {
        title: "把下一批叢集任務放到 CWP 上",
        body: "歡迎交流試用——叢集負載、產線測試，或 AI 基準。",
        contact: "聯絡我們",
      },
    },
    token: {
      eyebrow: "Token Service",
      subtitle: "簽發、配額並路由每一枚 LLM Token",
      desc: "Token Service 是 NewFW 的大型語言模型存取層。一套 {{OpenAI 相容 API}} 同時接入 50+ 公有模型以及你的私有 vLLM / Ollama / GPU 叢集。按組、配額與過期時間簽發 Key；簡單任務走本地，重推理走雲端；敏感 prompt 留在邊界內。",
      h1: "批量建 Key、設上限、過期並稽核用量",
      h2: "混合路由——公有 GPT / Claude / Gemini / DeepSeek / GLM 加上本地算力",
      h3: "Claude Code、Cursor、Cline、OpenCode 等 agent 共用一個閘道",
      h4: "錢包、模型市集，以及面向 99.9% 可用的故障切換",
      button: "開啟主控台",
      p1Title: "一把鑰匙，多種模型",
      p1Desc: "應用只保留一個 SDK 與端點，由 Token Service 把 Key 對應到模型組。",
      p2Title: "雲端 + 私有 GPU",
      p2Desc:
        "BYOC：接入 vLLM、Ollama 或 TGI。供應商抖動時故障切換；機密請求釘在本地模型。",
      p3Title: "面向 Agent",
      p3Desc:
        "SeaChart Code 擋在程式 agent 後面，企業把 Token 花費與資料路徑留在策略內。",
      p4Title: "維運看得見",
      p4Desc: "儀表板、查詢日誌、錢包與通道健康——Token 是庫存，不是試算表。",
    },
    firmware: {
      subtitle: "UEFI UniversalPayload 韌體",
      desc: "NewFW UEFI UniversalPayload 韌體基於開源專案 {{Linux as the firmware}}，整合 {{u-root}} 以 Go 實作的標準 Linux 工具，以及 {{USF UniversalPayload}}，用於快速驗證與部署。同一套韌體涵蓋雲端韌體與 SlimBMC 板級管理。",
      imgAlt: "NewFW 基於 USF 的韌體架構總覽",
    },
    about: {
      kicker: "公司",
      title: "關於 NewFW",
      lead: "NewFW 打造從韌體到 Token 的 AI 數據基礎設施：主機與 BMC 韌體、CXL 記憶體、面向 AI 資料路徑的 QLC 儲存、叢集負載主控台，以及大型語言模型 Token 閘道。",
      talk: "關於 BDF、韌體或 Token Service，歡迎聯絡：",
    },
    blog: {
      kicker: "洞察",
      title: "部落格",
      subtitle: "MLPerf Storage 成績、CWP 方法論，以及 NewFW 工程團隊的筆記。",
      readMore: "閱讀全文",
      back: "返回部落格",
      allPosts: "全部文章",
      toc: "目錄",
    },
    footer: {
      tagline: "AI 數據基礎設施，從韌體到 Token。",
      products: "產品",
      solutions: "解決方案",
      contact: "聯絡",
      about: "關於我們",
      blog: "部落格",
      brief: "BDF 資料手冊",
      copyright: "Copyright © 2026 NewFW 保留所有權利。",
    },
  },
};
